import { IValidReturn, IValidationFunction } from "../../utility";
import { CUSTOM_DATASET_VARIABLE_WARNING } from "./customDatasetVariable";
import { normaliseDatasetVariable } from "./normaliseDatasetVariable";

/**
 * Builds a successful validation response for dataset.variable validation.
 *
 * @param valid - Whether the candidate value is valid.
 * @param validationText - Feedback text to display for the validation result.
 * @param warning - Whether the valid result should be shown as a warning.
 * @returns A successful validation result payload.
 */
const buildDatasetVariableValidationResult = (
  valid: boolean,
  validationText: string,
  warning: boolean = false,
): IValidReturn => ({
  success: true,
  data: {
    valid,
    validationText,
    warning,
  },
});

/**
 * Validates a dataset.variable value against the list supplied through
 * `target.options.existingValues`.
 *
 * @param _backEndUrl - Unused validation URL parameter required by the shared signature.
 * @param target - Validation target containing the candidate value and existing variables.
 * @param _controller - Unused abort controller parameter required by the shared signature.
 * @returns A valid, warning, or invalid result for the candidate dataset.variable value.
 */
export const isUnlistedDatasetVariableValue: IValidationFunction = async (
  _backEndUrl,
  target,
  _controller,
) => {
  const trimmedValue = target.value.trim();
  const normalisedValue = normaliseDatasetVariable(trimmedValue);
  const existingValues = (target.options?.existingValues ?? []).map((value) => value.toUpperCase());
  const upperTrimmedValue = trimmedValue.toUpperCase();

  if (existingValues.includes(upperTrimmedValue)) {
    return buildDatasetVariableValidationResult(true, "Valid dataset and variable");
  }

  if (normalisedValue) {
    return buildDatasetVariableValidationResult(true, CUSTOM_DATASET_VARIABLE_WARNING, true);
  }

  return buildDatasetVariableValidationResult(false, "");
};
